import type { CoverageReportRow } from './coverageReport'

/**
 * The daily assignment sheet (docs/product/reports.md): the daily assignment
 * board for one date and one team as a page a supervisor can post or hand out.
 * The team's assignments in sort priority order, the people on each with their
 * shift times and activity, and the on-duty people with no assignment last.
 * Pure, so the page and the exports read one result.
 */
export type SheetEntry = {
  employeeId: string
  identifier: string
  employee: string
  teamId: string
  statusCategory: string
  startTime: string | null
  endTime: string | null
  assignmentId: string | null
  assignment: string | null
  sortPriority: number | null
  // The activity the assignment defaults to, from the activity catalog.
  activity: string | null
}

export type SheetPerson = {
  employeeId: string
  identifier: string
  employee: string
  startTime: string | null
  endTime: string | null
  activity: string | null
}

export type SheetGroup = {
  key: string
  assignment: string
  people: SheetPerson[]
  // The staffing target's minimum for the assignment on this date, where one
  // applies.
  required: number | null
}

export const UNASSIGNED_GROUP = 'Unassigned'

// On duty is a work entry, less anyone whose full-day leave was approved: the
// same rule the daily assignment board applies.
export function buildAssignmentSheet(
  entries: SheetEntry[],
  teamId: string,
  onApprovedLeave: Set<string>,
  coverage: CoverageReportRow[],
): SheetGroup[] {
  const onDuty = entries.filter((entry) => entry.teamId === teamId && entry.statusCategory === 'work' && !onApprovedLeave.has(entry.employeeId))

  const groups = new Map<string, { group: SheetGroup; priority: number }>()
  for (const entry of onDuty) {
    const key = entry.assignmentId ?? UNASSIGNED_GROUP
    const existing = groups.get(key)
    const person: SheetPerson = {
      employeeId: entry.employeeId,
      identifier: entry.identifier,
      employee: entry.employee,
      startTime: entry.startTime,
      endTime: entry.endTime,
      activity: entry.activity,
    }
    if (existing) {
      existing.group.people.push(person)
      continue
    }
    const target = entry.assignmentId
      ? coverage.find((row) => row.teamId === teamId && row.assignmentId === entry.assignmentId && row.required !== null)
      : undefined
    groups.set(key, {
      group: { key, assignment: entry.assignment ?? UNASSIGNED_GROUP, people: [person], required: target?.required ?? null },
      // Unassigned people are listed after every assignment.
      priority: entry.assignmentId ? (entry.sortPriority ?? Number.MAX_SAFE_INTEGER - 1) : Number.MAX_SAFE_INTEGER,
    })
  }

  return Array.from(groups.values())
    .sort((a, b) => a.priority - b.priority || a.group.assignment.localeCompare(b.group.assignment))
    .map(({ group }) => ({
      ...group,
      people: [...group.people].sort((a, b) => (a.startTime ?? '').localeCompare(b.startTime ?? '') || a.employee.localeCompare(b.employee)),
    }))
}

export function sheetHeadcount(groups: SheetGroup[]): number {
  return groups.reduce((sum, group) => sum + group.people.length, 0)
}

export function timeRange(person: Pick<SheetPerson, 'startTime' | 'endTime'>, format: (time: string) => string): string {
  return person.startTime && person.endTime ? `${format(person.startTime)} - ${format(person.endTime)}` : 'Times not set'
}
